/**
 * Trade-building helpers — converts public API params into contract-ready structs.
 */

import type { ApiOpen, ApiBuilder } from './api-schema.js';
import type { OstiumTrade, BuilderFee, OpenOrderType } from './ostium-schema.js';
import { parseUsdc, parsePrice, parseLeverage } from './decimal.js';
import { extractOpenType, validateTpSlDirection } from './validation.js';
import { bpsToContractBuilderFee } from './builder-fee.js';
import { MIN_OPEN_SIZE_USD } from './precision.js';

export function buildTrade(
  open: ApiOpen,
  traderAddress: string
): { trade: OstiumTrade; orderType: OpenOrderType } {
  const collateralUsd = parseFloat(open.s);
  if (collateralUsd < MIN_OPEN_SIZE_USD) {
    throw new Error(`Collateral ${open.s} below minimum ${MIN_OPEN_SIZE_USD}`);
  }

  const leverageNum = parseFloat(open.l);
  const collateral = parseUsdc(open.s);
  const openPrice = parsePrice(open.p);
  const leverage = parseLeverage(leverageNum);
  const tp = open.tp ? parsePrice(open.tp) : 0n;
  const sl = open.sl ? parsePrice(open.sl) : 0n;

  if (tp > 0n || sl > 0n) {
    validateTpSlDirection(open, tp, sl, openPrice);
  }

  const trade: OstiumTrade = {
    collateral,
    openPrice,
    tp,
    sl,
    trader: traderAddress as `0x${string}`,
    leverage,
    pairIndex: BigInt(open.a),
    index: 0n,
    buy: open.b,
    isDayTrade: open.d ?? false,
  };

  return { trade, orderType: extractOpenType(open) };
}

export function buildBuilderFee(builder?: ApiBuilder): BuilderFee {
  if (!builder) {
    return {
      builder: '0x0000000000000000000000000000000000000000',
      builderFee: 0n,
    };
  }
  // builder.f is human bps (e.g. 10 = 10 bps / 0.1%, 2.55 = 2.55 bps).
  return {
    builder: builder.b as `0x${string}`,
    builderFee: bpsToContractBuilderFee(builder.f),
  };
}
